// Replaces ProtectedRoute for /shared/:token only (architecture sprint-05 §6.6; sprint-06 §6.2, F71/F72).
// Signed in: the shell + SharedReportPage, unchanged. Signed out: try the public endpoint first; an
// active PUBLIC link renders the bare report, a 400/404 (private, expired, revoked, unknown, malformed:
// indistinguishable on purpose) takes the sprint-5 path (save return-to, go to sign-in), and any other
// failure shows a bare retry card that reveals nothing about the link.
import { useAuth } from '@skaleminds/jig-auth-sdk-ui';
import { lazy, Suspense, useCallback, useEffect, useRef, type ReactNode } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { usePublicSharedReport } from '@/hooks/use-share-links';
import { ApiClientError } from '@/lib/api';
import { clearReturnTo, peekReturnTo, saveReturnTo } from '@/lib/return-to';

// Lazy here (not in App) so recharts stays out of the entry chunk and a signed-out visitor of a
// private link never downloads it.
const PublicSharedReportPage = lazy(() =>
  import('@/pages/shared/PublicSharedReportPage').then((m) => ({ default: m.PublicSharedReportPage })),
);

const SIGN_IN_STATUSES = new Set([400, 404]);
const RATE_LIMITED_CODE = 'RATE_LIMITED';
const LOAD_ERROR_MESSAGE = "Couldn't load this report.";

function leadsToSignIn(error: unknown): boolean {
  return error instanceof ApiClientError && SIGN_IN_STATUSES.has(error.statusCode);
}

function BareSkeleton(): JSX.Element {
  return (
    <main className="min-h-dvh bg-background px-4 py-6 md:px-8" aria-busy="true">
      <div className="mx-auto max-w-5xl space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-96 w-full" />
      </div>
    </main>
  );
}

function PublicLoadError({ error, onRetry, onSignIn }: { error: unknown; onRetry: () => void; onSignIn: () => void }): JSX.Element {
  const message = error instanceof ApiClientError && error.code === RATE_LIMITED_CODE ? error.message : LOAD_ERROR_MESSAGE;
  return (
    <main className="flex min-h-dvh items-center justify-center bg-background px-4 py-10 text-foreground">
      <div className="card w-full max-w-sm space-y-4 p-6 text-center">
        <p className="text-sm">{message}</p>
        <div className="flex justify-center gap-2">
          <Button variant="outline" size="sm" onClick={onRetry}>
            Try again
          </Button>
          <Button size="sm" onClick={onSignIn}>
            Sign in
          </Button>
        </div>
      </div>
    </main>
  );
}

export function SharedLinkGate({ children }: { children: ReactNode }): JSX.Element | null {
  const { user, isLoading } = useAuth();
  const { token = '' } = useParams<{ token: string }>();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const signedOut = !isLoading && !user;
  const publicReport = usePublicSharedReport(token, { enabled: signedOut });
  const redirected = useRef(false);

  const goToSignIn = useCallback((): void => {
    saveReturnTo(pathname);
    navigate('/login', { replace: true });
  }, [pathname, navigate]);

  useEffect(() => {
    if (isLoading) return;
    if (user) {
      // Arrived here signed in: a pending return-to for this very link is now satisfied.
      if (peekReturnTo() === pathname) clearReturnTo();
      return;
    }
    if (publicReport.status === 'error' && leadsToSignIn(publicReport.error) && !redirected.current) {
      redirected.current = true;
      goToSignIn();
    }
  }, [isLoading, user, publicReport.status, publicReport.error, pathname, goToSignIn]);

  if (isLoading) return null;
  if (user) return <>{children}</>;
  if (publicReport.isPending) return <BareSkeleton />;
  if (publicReport.isSuccess) {
    return (
      <Suspense fallback={<BareSkeleton />}>
        <PublicSharedReportPage report={publicReport.data.report} token={token} />
      </Suspense>
    );
  }
  if (leadsToSignIn(publicReport.error)) return null;
  return <PublicLoadError error={publicReport.error} onRetry={() => void publicReport.refetch()} onSignIn={goToSignIn} />;
}
